import { DivIcon } from 'leaflet';
import { LocationType, FriendLocation } from '../../../types';

// Type for location type config
interface LocationTypeConfig {
  value: LocationType;
  label: string;
  icon: string;
  color: string;
}

// Helper to get photo URL or null
type PhotoUrlGetter = (photo?: string) => string | null;

/**
 * Custom blue marker for user's position
 */
export const userIcon = new DivIcon({
  className: 'user-location-marker',
  html: '<div class="user-marker-dot"></div>',
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

/**
 * Custom orange marker for user's position when it's the route origin
 */
export const userRouteOriginIcon = new DivIcon({
  className: 'user-location-marker route-origin',
  html: '<div class="user-marker-dot route-origin"></div>',
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

/**
 * Custom icon for preview marker (when selecting a location)
 */
export const previewIcon = new DivIcon({
  className: 'preview-location-marker',
  html: '<div class="preview-marker-dot"></div>',
  iconSize: [40, 40],
  iconAnchor: [20, 40],
});

/**
 * Custom icon for selected place marker
 */
export const selectedPlaceIcon = new DivIcon({
  className: 'selected-place-marker',
  html: '<div class="selected-place-marker-dot">📍</div>',
  iconSize: [40, 40],
  iconAnchor: [20, 40],
});

/**
 * Create friend marker icon with profile photo or initials
 * @param isRoutePoint - If true, adds orange border to indicate this friend is part of the current route
 * @param isRouteDestination - If true, adds destination flag emoji (only for destination, not origin/waypoint)
 * @param stepNumber - If provided, shows step number badge (for waypoints: 1, 2, 3...)
 * @param customBorderColor - If provided, adds a custom colored border (e.g., '#e1723c' for orange)
 */
export const createFriendIcon = (
  photoUrl: string | null,
  firstName: string,
  lastName: string,
  isRoutePoint: boolean = false,
  isRouteDestination: boolean = false,
  stepNumber?: number,
  customBorderColor?: string
): DivIcon => {
  const routeClass = isRoutePoint ? ' route-point' : '';
  const destinationClass = isRouteDestination ? ' route-destination' : '';
  const customBorderClass = customBorderColor ? ' custom-border' : '';
  const customBorderStyle = customBorderColor ? `style="border-color: ${customBorderColor};"` : '';
  // Step badge HTML: shows number for waypoints (not for destination which uses flag)
  const stepBadge = stepNumber !== undefined && !isRouteDestination
    ? `<span class="route-step-badge">${stepNumber}</span>`
    : '';

  if (photoUrl) {
    return new DivIcon({
      className: `friend-photo-marker${routeClass}${destinationClass}${customBorderClass}`,
      html: `
        <div class="friend-photo-marker-container${routeClass}${destinationClass}${customBorderClass}" ${customBorderStyle}>
          <img src="${photoUrl}" alt="Profile" class="friend-photo-marker-img" />
          ${stepBadge}
        </div>
      `,
      iconSize: [44, 44],
      iconAnchor: [22, 44],
      popupAnchor: [0, -49],
    });
  } else {
    // Fallback to marker with initials
    const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    return new DivIcon({
      className: `friend-default-marker${routeClass}${destinationClass}${customBorderClass}`,
      html: `
        <div class="friend-default-marker-container${routeClass}${destinationClass}${customBorderClass}" ${customBorderStyle}>
          <span class="friend-initials">${initials}</span>
          ${stepBadge}
        </div>
      `,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -45],
    });
  }
};

/**
 * Get temporary location marker icon based on type with profile photo
 */
export const getTempLocationIcon = (
  type: LocationType,
  photoUrl: string | null,
  firstName: string,
  lastName: string,
  locationTypes: LocationTypeConfig[]
): DivIcon => {
  const locationTypeConfig = locationTypes.find(lt => lt.value === type) || locationTypes.find(lt => lt.value === 'other')!;

  if (photoUrl) {
    // Marker with profile photo and type emoji badge
    return new DivIcon({
      className: 'temp-location-marker-with-photo',
      html: `
        <div class="temp-marker-photo-container">
          <img src="${photoUrl}" alt="Profile" class="temp-marker-photo-img" />
          <span class="temp-marker-type-badge" style="background-color: ${locationTypeConfig.color}">
            ${locationTypeConfig.icon}
          </span>
        </div>
      `,
      iconSize: [44, 44],
      iconAnchor: [22, 44],
      popupAnchor: [0, -49],
    });
  } else {
    // Fallback to initials with type emoji
    const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    return new DivIcon({
      className: 'temp-location-marker',
      html: `
        <div class="temp-marker-container" style="background-color: ${locationTypeConfig.color}">
          <span class="temp-marker-initials">${initials}</span>
          <span class="temp-marker-type-badge-small">${locationTypeConfig.icon}</span>
        </div>
      `,
      iconSize: [36, 36],
      iconAnchor: [18, 36],
      popupAnchor: [0, -41],
    });
  }
};

/**
 * Get my temporary location marker icon (with special style)
 */
export const getMyTempLocationIcon = (
  type: LocationType,
  locationTypes: LocationTypeConfig[]
): DivIcon => {
  const locationTypeConfig = locationTypes.find(lt => lt.value === type) || locationTypes.find(lt => lt.value === 'other')!;

  return new DivIcon({
    className: 'my-temp-location-marker',
    html: `
      <div class="my-temp-marker-container" style="background-color: ${locationTypeConfig.color}">
        <span class="temp-marker-icon">${locationTypeConfig.icon}</span>
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -45],
  });
};

/**
 * Create clustered marker icon with stacked photos
 * Shows up to 3 photos stacked, with a badge showing total count
 */
export const createClusteredFriendIcon = (
  friends: FriendLocation[],
  getPhotoUrl: PhotoUrlGetter
): DivIcon => {
  const count = friends.length;
  const maxPhotosToShow = 3;
  const photosToShow = friends.slice(0, maxPhotosToShow);

  // Generate HTML for stacked photos
  const photosHtml = photosToShow
    .map((friend, index) => {
      const photoUrl = getPhotoUrl(friend.profile_photo);
      const zIndex = maxPhotosToShow - index;
      const offset = index * 8; // 8px offset for each photo

      if (photoUrl) {
        return `
          <div class="clustered-photo" style="z-index: ${zIndex}; left: ${offset}px;">
            <img src="${photoUrl}" alt="${friend.first_name}" />
          </div>
        `;
      } else {
        const initials = `${friend.first_name.charAt(0)}${friend.last_name.charAt(0)}`.toUpperCase();
        return `
          <div class="clustered-photo clustered-photo-initials" style="z-index: ${zIndex}; left: ${offset}px;">
            <span>${initials}</span>
          </div>
        `;
      }
    })
    .join('');

  return new DivIcon({
    className: 'clustered-friend-marker',
    html: `
      <div class="clustered-marker-container">
        <div class="clustered-photos">
          ${photosHtml}
        </div>
        <div class="cluster-count-badge">${count}</div>
      </div>
    `,
    iconSize: [66, 50], // Width accommodates offset photos
    iconAnchor: [33, 50],
    popupAnchor: [0, -55],
  });
};
